import type { PublicationShareAnchor, PublicationShareEntry } from '@/types/messages/base';
import { MESSAGE_URL_PATTERN } from '@/util/messageLinks';

/**
 * A work cited in the message text: the words that cite it, from `start` to
 * `end` in the body, and the reference number shown after them.
 */
export interface InlineCitation {
  start: number;
  end: number;
  number: number;
}

/**
 * A run of message text split for rendering: plain text, the words that cite
 * a work (tappable, like its chip), and the chip itself.
 */
export type CitedTextPiece =
  | { kind: 'text'; text: string }
  | { kind: 'cited'; text: string; number: number }
  | { kind: 'citation'; number: number };

/**
 * The reference numbers to place in a message's own text, each after the
 * first place the server found that work cited. The number is the server's,
 * the same one the footer and the statement chips show.
 *
 * Anchors are positions in the body the server read. One whose text no longer
 * matches the body at that position is stale, such as after an edit, and is
 * skipped for the entry's next anchor rather than drawn in the wrong place. A
 * work with no anchor that still matches gets no chip.
 */
export function buildInlineCitations(
  body: string,
  entries: readonly PublicationShareEntry[]
): InlineCitation[] {
  return entries.flatMap((entry) => {
    const anchor = entry.anchors.find((candidate) => anchorMatches(body, candidate));

    return anchor ? [{ ...expandToEnclosingLink(body, anchor), number: entry.number }] : [];
  });
}

/**
 * The reference numbers a tap can open: those the reference list holds, which
 * needs their details loaded. A number for any other entry would look like a
 * button and do nothing, as when the publications request is still pending,
 * fails or is refused, or when it is the author's pending request. Inline
 * citations and statement chips both go by this, so they agree on what opens.
 */
export function openableNumbers(references: readonly { number: number }[]): Set<number> {
  return new Set(references.map((reference) => reference.number));
}

/** The entries a number placed in the text can open; see `openableNumbers`. */
export function withOpenableEntries(
  entries: readonly PublicationShareEntry[],
  references: readonly { number: number }[]
): PublicationShareEntry[] {
  const openable = openableNumbers(references);

  return entries.filter((entry) => openable.has(entry.number));
}

const anchorMatches = (body: string, anchor: PublicationShareAnchor) =>
  anchor.end > anchor.start && body.slice(anchor.start, anchor.end) === anchor.text;

/**
 * Widens a citation that falls inside a link to the whole link, so a chip
 * never splits a URL and the link stays one link: an NCT number can sit
 * inside a registry link the expert pasted.
 */
export function expandToEnclosingLink(
  body: string,
  span: { start: number; end: number }
): { start: number; end: number } {
  const linkPattern = new RegExp(MESSAGE_URL_PATTERN.source, 'gi');

  for (const link of body.matchAll(linkPattern)) {
    const linkEnd = link.index + link[0].length;

    if (span.start < linkEnd && link.index < span.end) {
      return { start: Math.min(span.start, link.index), end: Math.max(span.end, linkEnd) };
    }
  }

  return { start: span.start, end: span.end };
}

/**
 * Splits one run of a message body at the citations that touch it. `start` is
 * where the run begins in the body. Cited words are clipped to the run, since
 * a citation can cross into a neighbouring run, and the chip goes in the run
 * holding the citation's last character, so it follows the words it cites.
 */
export function splitAtCitations(
  text: string,
  start: number,
  citations: readonly InlineCitation[]
): CitedTextPiece[] {
  const runEnd = start + text.length;

  const pieces: CitedTextPiece[] = [];

  let cursor = 0;

  const touching = citations
    .filter((citation) => citation.end > start && citation.start < runEnd)
    .sort((first, second) => first.start - second.start);

  for (const citation of touching) {
    const citedStart = Math.max(citation.start - start, cursor);

    const citedEnd = Math.min(citation.end - start, text.length);

    if (citedStart > cursor) {
      pieces.push({ kind: 'text', text: text.slice(cursor, citedStart) });
    }

    if (citedEnd > citedStart) {
      pieces.push({
        kind: 'cited',
        text: text.slice(citedStart, citedEnd),
        number: citation.number,
      });
    }

    cursor = Math.max(cursor, citedEnd);

    if (citation.end <= runEnd) {
      pieces.push({ kind: 'citation', number: citation.number });
    }
  }

  if (cursor < text.length) {
    pieces.push({ kind: 'text', text: text.slice(cursor) });
  }

  return pieces;
}
